import { type ReactNode, type RefObject, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { RemoveScroll } from "react-remove-scroll";

/**
 * The modal layer of a desktop dialog: the backdrop, and the card it centres.
 * The card's look belongs to its caller (the settings modal and the
 * JuisciGPT history draw theirs differently); this owns what every dialog must
 * do the same way.
 *
 * It also gives every scroller inside the card the panel scrollbar
 * (`scrollbar-subtle`), whatever the caller's body scrolls.
 *
 * UI machinery, so it owns the keyboard (`useModalKeyboard`): Escape closes
 * it, and focus moves into the card, stays there while it is open, and goes
 * back to where it was when it closes. Without that, Tab kept walking the page
 * behind an `aria-modal` dialog. Whether it is open is the caller's: it renders
 * this or it does not.
 */
export function DesktopDialogOverlay({
  label,
  onClose,
  cardClassName,
  children,
}: {
  /** The dialog's accessible name: its title. */
  label: string;
  onClose: () => void;
  cardClassName: string;
  children: ReactNode;
}): React.JSX.Element {
  const cardRef = useRef<HTMLDivElement>(null);
  useModalKeyboard({ cardRef, onClose, enabled: true });

  return createPortal(
    <RemoveScroll enabled>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="fixed inset-0 z-1000 flex items-center justify-center bg-ds-black/35 p-6 scrollbar-subtle"
        onClick={onClose}
      >
        <div
          ref={cardRef}
          className={cardClassName}
          // The card is inside the backdrop, so a click on it would close the
          // dialog through the backdrop's handler.
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          {children}
        </div>
      </div>
    </RemoveScroll>,
    document.body
  );
}

/**
 * The keyboard contract of a modal surface: focus moves into the card, Tab
 * stays inside it, Escape closes it, and focus goes back where it was on close.
 *
 * `enabled` is false while another surface is stacked on top: the settings
 * modal hands the keyboard to the dialog it opened, or Escape would close both
 * and Tab would pull focus back out of the dialog.
 */
export function useModalKeyboard({
  cardRef,
  onClose,
  enabled,
}: {
  cardRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  enabled: boolean;
}): void {
  const enabledAtMount = useRef(enabled);
  useEffect(() => {
    const card = cardRef.current;
    if (card === null)
      throw new Error("should not happen: modal card unmounted");
    const previouslyFocused = document.querySelector<HTMLElement>(":focus");
    // Opened with a surface already on top (a deep link straight into a
    // dialog): that surface takes the focus, which this card must not steal.
    if (!enabledAtMount.current)
      return () => {
        restoreFocus(previouslyFocused);
      };
    // `data-autofocus` names the field a dialog opens on (the name input);
    // otherwise the first control, which is the close button.
    const preferred = card.querySelector<HTMLElement>("[data-autofocus]");
    const first = preferred === null ? focusables(card).at(0) : preferred;
    if (first !== undefined) first.focus();
    return () => {
      restoreFocus(previouslyFocused);
    };
  }, [cardRef]);

  useEffect(() => {
    if (!enabled) return;
    function handleKeyDown(event: KeyboardEvent): void {
      const card = cardRef.current;
      if (card === null)
        throw new Error("should not happen: modal card unmounted");
      const active = document.activeElement;
      // A menu open inside the card answers its own keys (`usePopoverAnchor`):
      // Escape and Tab close it and return the focus to its trigger.
      if (active !== null && active.closest('[role="menu"]') !== null) return;
      if (event.key === "Escape") {
        // Handled: a later listener (the composer's Escape-to-stop) runs
        // after this close has already removed the dialog.
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusables(card);
      const first = items.at(0);
      const last = items.at(-1);
      if (first === undefined || last === undefined) {
        event.preventDefault();
        return;
      }
      const outside = active === null || !card.contains(active);
      if (event.shiftKey && (outside || active === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (outside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [cardRef, onClose, enabled]);
}

/**
 * Back where the focus was, unless that is behind a modal still open (a
 * dialog a deep link opened over the settings modal was opened from the page):
 * then into that modal, so the keyboard does not reach the page behind it.
 */
function restoreFocus(previouslyFocused: HTMLElement | null): void {
  const modals = [
    ...document.querySelectorAll<HTMLElement>('[aria-modal="true"]'),
  ];
  const top = modals.at(-1);
  if (
    top === undefined ||
    (previouslyFocused !== null && top.contains(previouslyFocused))
  ) {
    if (previouslyFocused !== null) previouslyFocused.focus();
    return;
  }
  const first = focusables(top).at(0);
  if (first !== undefined) first.focus();
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)];
}
